import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import { API_URL } from '../../config/api';
import { fetchUsers } from '../../store/slices/userSlice';
import { fetchChannels } from '../../store/slices/channelSlice';
import { uploadFilesWithProgress } from '../../utils/uploadFilesWithProgress';
import { buildLegacyFileUrl, extractInternalFileKey, getPresignedFileUrl } from '../../services/fileUrlService';
import {
  ALL_MENTION_USERNAME,
  findMentionTriggerIndex,
  findUserByMention,
  isEveryoneMention,
} from '../../utils/mentionDisplayUtils';
import PresignedAvatar from './PresignedAvatar';
import { copyTextToClipboard } from '../../utils/copyText';
import {
  MAX_COLS,
  MAX_ROWS,
  clampTableDim,
  emptyTableData,
  htmlHasNonTableContent,
  htmlTableToTableData,
  readTableFromDom,
  applyTableDataToNode,
  splitPlainIntoTextAndTable,
  textToTableData,
  toggleCheckboxPrefix,
} from '../../utils/noteTableUtils';
import NoteTableWorkspaceModal from './NoteTableWorkspaceModal';
import {
  attachNoteTableChrome,
  ensureNoteTableOnEditor,
  getLastNoteTableCell,
  insertNoteTableAtSelection,
  quillSelectionToTableData,
  registerNoteTableBlot,
  syncTableNode,
} from './noteTableBlot';
import 'react-quill/dist/quill.snow.css';

registerNoteTableBlot(Quill);
registerNoteTableBlot(ReactQuill.Quill);

export const COMPACT_QUILL_FORMATS = [
  'bold', 'italic', 'underline', 'strike',
  'header',
  'color',
  'list', 'bullet',
  'link', 'image',
  'code', 'code-block',
  'noteTable',
];

export const toQuillCompatibleHtml = (html) => {
  let next = String(html || '').trim();
  if (!next) return '';
  next = next.replace(/<\/?(html|body|head)[^>]*>/gi, '');
  next = next.replace(/<style[\s\S]*?<\/style>/gi, '');
  next = next.replace(/<!--[\s\S]*?-->/g, '');
  if (!/^<[a-z]/i.test(next)) {
    next = next
      .split(/\n+/)
      .map((line) => `<p>${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') || '<br>'}</p>`)
      .join('');
  }
  return next;
};

const deltaHasContent = (delta) => {
  const ops = delta?.ops || [];
  return ops.some((op) => {
    if (typeof op.insert === 'string') return op.insert.replace(/\n/g, '').trim().length > 0;
    return Boolean(op.insert && typeof op.insert === 'object');
  });
};

const convertHtmlToDelta = (editor, html) => {
  const cleaned = toQuillCompatibleHtml(html);
  if (!cleaned || !editor?.clipboard?.convert) return null;
  const tryConvert = (arg) => {
    try {
      return editor.clipboard.convert(arg);
    } catch {
      return null;
    }
  };
  // Quill 1 expects a HTML string. Passing { html } yields an empty note.
  const fromString = tryConvert(cleaned);
  if (deltaHasContent(fromString)) return fromString;
  return tryConvert({ html: cleaned });
};

const isEmptyQuillHtml = (html) => {
  const text = String(html || '')
    .replace(/<p><br\s*\/?><\/p>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return !text;
};

export const setQuillHtml = (editor, html) => {
  const next = toQuillCompatibleHtml(html);
  if (!next) return '';
  if (!editor) return next;
  const delta = convertHtmlToDelta(editor, next);
  try {
    if (deltaHasContent(delta) && editor.setContents) {
      editor.setContents(delta, 'silent');
    } else if (typeof editor.clipboard?.dangerouslyPasteHTML === 'function') {
      editor.clipboard.dangerouslyPasteHTML(0, next, 'silent');
    } else if (editor.root) {
      editor.root.innerHTML = next;
    }
  } catch {
    if (editor.root) editor.root.innerHTML = next;
  }
  let applied = editor.root?.innerHTML || '';
  if (isEmptyQuillHtml(applied) && editor.root) {
    editor.root.innerHTML = next;
    applied = editor.root.innerHTML;
  }
  return isEmptyQuillHtml(applied) ? next : applied;
};

const insertDeltaAtSelection = (editor, delta) => {
  if (!editor || !delta) return false;
  const Delta = Quill.import('delta');
  const range = editor.getSelection(true) || { index: Math.max(0, editor.getLength() - 1), length: 0 };
  const next = new Delta().retain(range.index).delete(range.length).concat(delta);
  editor.updateContents(next, 'user');
  return true;
};

const pasteNotesClipboard = (editor, clipboardData) => {
  if (!editor || !clipboardData) return false;
  const html = clipboardData.getData?.('text/html') || '';
  const text = clipboardData.getData?.('text/plain') || '';
  const hasTable = /<table[\s>]/i.test(html);
  const mixedHtml = hasTable && htmlHasNonTableContent(html);
  const tableOnlyHtml = hasTable && !htmlHasNonTableContent(html);

  if (tableOnlyHtml) {
    const tableData = htmlTableToTableData(html);
    if (tableData) {
      insertNoteTableAtSelection(editor, tableData);
      return true;
    }
  }

  if (html.trim() && (mixedHtml || !tableOnlyHtml)) {
    const delta = convertHtmlToDelta(editor, html);
    if (deltaHasContent(delta)) {
      return insertDeltaAtSelection(editor, delta);
    }
  }

  const { prefix, table } = splitPlainIntoTextAndTable(text);
  if (prefix) {
    const range = editor.getSelection(true) || { index: Math.max(0, editor.getLength() - 1), length: 0 };
    if (range.length) editor.deleteText(range.index, range.length, 'user');
    const chunk = prefix.endsWith('\n') ? prefix : `${prefix}\n`;
    editor.insertText(range.index, chunk, 'user');
    editor.setSelection(range.index + chunk.length, 0, 'silent');
  }
  if (table) {
    insertNoteTableAtSelection(editor, table);
    return true;
  }
  if (prefix) return true;
  if (text.trim()) {
    const range = editor.getSelection(true) || { index: Math.max(0, editor.getLength() - 1), length: 0 };
    if (range.length) editor.deleteText(range.index, range.length, 'user');
    editor.insertText(range.index, text, 'user');
    return true;
  }
  return false;
};

const readStoredWidth = (node) => {
  if (!node) return null;
  const raw = node.getAttribute?.('data-width')
    || node.getAttribute?.('width')
    || String(node.style?.width || '').replace('px', '');
  const width = Number.parseInt(raw, 10);
  return Number.isFinite(width) && width > 0 ? width : null;
};

const applyStoredWidth = (node, widthPx) => {
  if (!node) return;
  const width = Number.parseInt(widthPx, 10);
  if (!Number.isFinite(width) || width <= 0) return;
  node.setAttribute('width', String(width));
  node.setAttribute('data-width', String(width));
  node.style.width = `${width}px`;
  node.style.height = 'auto';
  node.style.maxWidth = '100%';
};

const fileKeyFromImageNode = (node) => {
  if (!node) return '';
  return extractInternalFileKey(
    node.getAttribute?.('data-file-key')
    || node.getAttribute?.('src')
    || '',
  );
};

const persistImageAttrsOnNode = (node) => {
  if (!node) return;
  const key = fileKeyFromImageNode(node);
  if (key) node.setAttribute('data-file-key', key);
  const width = readStoredWidth(node);
  if (width) applyStoredWidth(node, width);
};

const restoreImageWidths = (root, sourceHtml) => {
  if (!root) return;
  const saved = [];
  if (sourceHtml) {
    const holder = document.createElement('div');
    holder.innerHTML = sourceHtml;
    saved.push(...holder.querySelectorAll('img'));
  }
  root.querySelectorAll('img').forEach((img, index) => {
    const fromSaved = saved[index];
    const key = fileKeyFromImageNode(img) || fileKeyFromImageNode(fromSaved);
    if (key) img.setAttribute('data-file-key', key);
    const width = readStoredWidth(img) || readStoredWidth(fromSaved);
    if (width) applyStoredWidth(img, width);
  });
};

const hydrateEditorImages = async (root) => {
  if (!root) return;
  const images = [...root.querySelectorAll('img')];
  await Promise.all(images.map(async (img) => {
    persistImageAttrsOnNode(img);
    const key = img.getAttribute('data-file-key');
    if (!key) return;
    try {
      const url = await getPresignedFileUrl(key) || buildLegacyFileUrl(key);
      if (url) img.setAttribute('src', url);
    } catch {
      const fallback = buildLegacyFileUrl(key);
      if (fallback) img.setAttribute('src', fallback);
    }
  }));
};

const registerResizableImage = () => {
  if (registerResizableImage.done) return;
  registerResizableImage.done = true;
  try {
    const Image = Quill.import('formats/image');
    class ResizableImage extends Image {
      static sanitize(url) {
        if (!url || typeof url !== 'string') return '//:0';
        const trimmed = url.trim();
        if (
          trimmed.startsWith('/api/files/')
          || trimmed.startsWith('/files/')
          || trimmed.startsWith('blob:')
          || trimmed.startsWith('data:image/')
        ) {
          return trimmed;
        }
        if (typeof super.sanitize === 'function') {
          return super.sanitize(trimmed);
        }
        return trimmed;
      }

      static create(value) {
        const src = value && typeof value === 'object'
          ? (value.src || value.url || '')
          : value;
        const node = super.create(typeof src === 'string' ? src : '');
        persistImageAttrsOnNode(node);
        if (value && typeof value === 'object') {
          applyStoredWidth(node, value.width);
          if (value.fileKey) node.setAttribute('data-file-key', value.fileKey);
        }
        return node;
      }

      static value(domNode) {
        return domNode.getAttribute('src') || '';
      }
    }
    Quill.register(ResizableImage, true);
  } catch (err) {
    console.warn('Could not register resizable image blot', err);
  }
};
registerResizableImage();

const MENTION_LIST_MAX_HEIGHT = 192;
const MENTION_LIST_GAP = 6;
const MENTION_LIST_Z_INDEX = 1400;

const CODE_COPY_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path></svg>`;
const CODE_COPIED_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"></path></svg>`;
const codeChromeSync = new WeakMap();

const sanitizeEditorHtml = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  const holder = document.createElement('div');
  holder.innerHTML = html;
  holder.querySelectorAll('[data-code-copy]').forEach((node) => node.remove());
  holder.querySelectorAll('img').forEach((img) => {
    persistImageAttrsOnNode(img);
    const key = img.getAttribute('data-file-key');
    if (key) {
      img.setAttribute('src', buildLegacyFileUrl(key));
    }
  });
  holder.querySelectorAll('.note-data-table').forEach((node) => {
    applyTableDataToNode(node, readTableFromDom(node));
  });
  return holder.innerHTML;
};

const collectCodeBlocks = (editorRoot) => {
  if (!editorRoot) return [];
  const blocks = [
    ...editorRoot.querySelectorAll('.ql-code-block-container'),
    ...editorRoot.querySelectorAll('pre.ql-syntax'),
    ...editorRoot.querySelectorAll('pre'),
  ];
  const seen = new Set();
  return blocks.filter((block) => {
    if (seen.has(block)) return false;
    if (block.closest('.ql-code-block-container') && !block.classList.contains('ql-code-block-container')) {
      return false;
    }
    seen.add(block);
    return true;
  });
};

const codeBlockLines = (block) => {
  const nested = [...block.querySelectorAll('.ql-code-block')];
  if (nested.length) {
    return nested.map((line) => String(line.textContent || '').replace(/\u00a0/g, ' '));
  }
  const raw = String(block.textContent || '').replace(/\u00a0/g, ' ');
  const lines = raw.split('\n');
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  return lines.length ? lines : [''];
};

const syncNoteChecklistHighlights = (editorRoot) => {
  if (!editorRoot) return;
  editorRoot.querySelectorAll('li').forEach((item) => {
    const list = item.getAttribute('data-list');
    item.classList.toggle('note-check-on', list === 'checked');
  });
};

const markCopied = (button, label) => {
  button.innerHTML = CODE_COPIED_ICON;
  button.setAttribute('data-copied', '1');
  button.setAttribute('aria-label', 'Copied');
  window.setTimeout(() => {
    button.innerHTML = CODE_COPY_ICON;
    button.removeAttribute('data-copied');
    button.setAttribute('aria-label', label);
  }, 1400);
};

const createCodeCopyButton = ({ className, label, getText }) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = className;
  button.setAttribute('aria-label', label);
  button.innerHTML = CODE_COPY_ICON;
  button.addEventListener('mousedown', (event) => {
    event.preventDefault();
    event.stopPropagation();
  });
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const text = String(getText() || '').replace(/\s+$/g, '');
    if (!text) return;
    copyTextToClipboard(text).then(() => markCopied(button, label)).catch(() => {});
  });
  return button;
};

const mountNoteCodeChrome = (container, editorRoot) => {
  if (!container || !editorRoot) return () => {};
  const computed = window.getComputedStyle(container);
  if (computed.position === 'static') {
    container.style.position = 'relative';
  }
  let chrome = container.querySelector(':scope > .note-code-chrome');
  if (!chrome) {
    chrome = document.createElement('div');
    chrome.className = 'note-code-chrome';
    container.appendChild(chrome);
  }

  let hoverHideTimer = 0;
  const hostByBlock = new WeakMap();

  const clearLineHover = () => {
    chrome.querySelectorAll('.note-code-line-hit.is-hovered').forEach((row) => {
      row.classList.remove('is-hovered');
    });
  };

  const sync = () => {
    const cRect = container.getBoundingClientRect();
    chrome.replaceChildren();
    collectCodeBlocks(editorRoot).forEach((block) => {
      const pRect = block.getBoundingClientRect();
      if (pRect.height < 8 || pRect.bottom < cRect.top || pRect.top > cRect.bottom) {
        return;
      }
      const styles = window.getComputedStyle(block);
      let lineHeight = parseFloat(styles.lineHeight);
      if (!lineHeight || Number.isNaN(lineHeight)) {
        lineHeight = (parseFloat(styles.fontSize) || 13) * 1.7;
      }
      const paddingTop = parseFloat(styles.paddingTop) || 0;
      const lines = codeBlockLines(block);
      const host = document.createElement('div');
      host.className = 'note-code-chrome-host';
      host.style.left = `${pRect.left - cRect.left}px`;
      host.style.top = `${pRect.top - cRect.top}px`;
      host.style.width = `${pRect.width}px`;
      host.style.height = `${pRect.height}px`;

      const gutter = document.createElement('div');
      gutter.className = 'note-code-gutter';
      gutter.style.paddingTop = `${paddingTop}px`;
      lines.forEach((_, index) => {
        const num = document.createElement('div');
        num.className = 'note-code-gutter-num';
        num.style.height = `${lineHeight}px`;
        num.textContent = String(index + 1);
        gutter.appendChild(num);
      });
      host.appendChild(gutter);

      const allBtn = createCodeCopyButton({
        className: 'note-code-copy',
        label: 'Copy all code',
        getText: () => codeBlockLines(block).join('\n'),
      });
      host.appendChild(allBtn);

      const lineLayer = document.createElement('div');
      lineLayer.className = 'note-code-lines';
      lineLayer.style.paddingTop = `${paddingTop}px`;
      lines.forEach((lineText) => {
        const row = document.createElement('div');
        row.className = 'note-code-line-hit';
        row.style.height = `${lineHeight}px`;
        row.appendChild(createCodeCopyButton({
          className: 'note-code-copy-line',
          label: 'Copy line',
          getText: () => lineText,
        }));
        lineLayer.appendChild(row);
      });
      host.appendChild(lineLayer);
      chrome.appendChild(host);
      hostByBlock.set(block, {
        host,
        lineHeight,
        paddingTop,
      });
    });
  };

  const onEditorMouseMove = (event) => {
    const block = event.target?.closest?.('pre, .ql-code-block-container');
    const meta = block ? hostByBlock.get(block) : null;
    window.clearTimeout(hoverHideTimer);
    if (!meta) {
      clearLineHover();
      return;
    }
    const rect = block.getBoundingClientRect();
    const y = event.clientY - rect.top - meta.paddingTop;
    const idx = Math.floor(y / meta.lineHeight);
    const rows = meta.host.querySelectorAll('.note-code-line-hit');
    rows.forEach((row, index) => {
      row.classList.toggle('is-hovered', index === idx);
    });
  };

  const onEditorMouseLeave = (event) => {
    if (chrome.contains(event.relatedTarget)) return;
    hoverHideTimer = window.setTimeout(clearLineHover, 160);
  };

  const onChromeMouseEnter = () => {
    window.clearTimeout(hoverHideTimer);
  };

  const onChromeMouseLeave = (event) => {
    if (editorRoot.contains(event.relatedTarget)) return;
    hoverHideTimer = window.setTimeout(clearLineHover, 80);
  };

  editorRoot.addEventListener('mousemove', onEditorMouseMove);
  editorRoot.addEventListener('mouseleave', onEditorMouseLeave);
  chrome.addEventListener('mouseenter', onChromeMouseEnter);
  chrome.addEventListener('mouseleave', onChromeMouseLeave);

  codeChromeSync.set(editorRoot, sync);
  const onScrollOrResize = () => sync();
  container.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);
  const observer = new MutationObserver(() => {
    window.requestAnimationFrame(sync);
  });
  observer.observe(editorRoot, { childList: true, subtree: true, characterData: true });
  sync();
  return () => {
    codeChromeSync.delete(editorRoot);
    window.clearTimeout(hoverHideTimer);
    editorRoot.removeEventListener('mousemove', onEditorMouseMove);
    editorRoot.removeEventListener('mouseleave', onEditorMouseLeave);
    chrome.removeEventListener('mouseenter', onChromeMouseEnter);
    chrome.removeEventListener('mouseleave', onChromeMouseLeave);
    container.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
    observer.disconnect();
    chrome.remove();
  };
};

const syncNoteCodeChrome = (editorRoot) => {
  if (editorRoot) codeChromeSync.get(editorRoot)?.();
};

const EVERYONE_MENTION = {
  id: '__mention_all__',
  username: ALL_MENTION_USERNAME,
  firstName: 'all',
  lastName: '',
  isEveryoneMention: true,
};

export const channelMentionToken = (channel) =>
  (channel?.name || '').trim().replace(/\s+/g, '-');

const isDirectChannel = (channel) =>
  channel?.type === 'direct' || channel?.type === 'DIRECT_MESSAGE';

const applyImageWidth = (img, widthPx, maxWidthPx) => {
  const maxWidth = Math.max(48, Math.round(maxWidthPx || widthPx));
  const width = Math.max(48, Math.min(maxWidth, Math.round(widthPx)));
  applyStoredWidth(img, width);
  img.removeAttribute('height');
};

const nextWidthForHandle = (dir, startWidth, dx, dy) => {
  if (dir === 'n') return startWidth - dy;
  if (dir === 's') return startWidth + dy;
  if (dir === 'e') return startWidth + dx;
  if (dir === 'w') return startWidth - dx;
  if (dir === 'ne') return startWidth + Math.max(dx, -dy);
  if (dir === 'nw') return startWidth + Math.max(-dx, -dy);
  if (dir === 'sw') return startWidth + Math.max(-dx, dy);
  return startWidth + Math.max(dx, dy);
};

const attachQuillImageResize = (editor, wrapperEl, onHtmlChange, resizingRef) => {
  if (!editor?.root || !wrapperEl) return () => {};

  let overlay = null;
  let activeImg = null;
  let dragging = false;

  const setResizing = (value) => {
    dragging = value;
    if (resizingRef) resizingRef.current = value;
    if (editor.root) {
      if (value) editor.root.dataset.imageResizing = '1';
      else delete editor.root.dataset.imageResizing;
    }
  };

  const emitChange = () => {
    if (typeof onHtmlChange === 'function') {
      onHtmlChange(sanitizeEditorHtml(editor.root.innerHTML));
    }
  };

  const maxImageWidth = () => Math.max(48, (editor.root?.clientWidth || wrapperEl.clientWidth || 240) - 8);

  const clearOverlay = () => {
    overlay?.remove();
    overlay = null;
    activeImg = null;
    setResizing(false);
  };

  const positionOverlay = () => {
    if (!overlay || !activeImg || !wrapperEl.contains(activeImg)) {
      clearOverlay();
      return;
    }
    const wrapRect = wrapperEl.getBoundingClientRect();
    const imgRect = activeImg.getBoundingClientRect();
    overlay.style.left = `${imgRect.left - wrapRect.left}px`;
    overlay.style.top = `${imgRect.top - wrapRect.top}px`;
    overlay.style.width = `${Math.max(24, imgRect.width)}px`;
    overlay.style.height = `${Math.max(24, imgRect.height)}px`;
  };

  const deleteActiveImage = () => {
    if (!activeImg) return;
    const img = activeImg;
    try {
      const blot = Quill.find(img);
      if (blot) {
        const index = editor.getIndex(blot);
        editor.deleteText(index, 1, 'user');
      } else {
        img.remove();
      }
    } catch {
      img.remove();
    }
    clearOverlay();
    emitChange();
  };

  const bindHandle = (handle) => {
    handle.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!activeImg) return;
      setResizing(true);
      const dir = handle.getAttribute('data-dir') || 'se';
      const startX = event.clientX;
      const startY = event.clientY;
      const startWidth = activeImg.getBoundingClientRect().width;
      const capId = event.pointerId;
      handle.setPointerCapture?.(capId);
      const onMove = (moveEvent) => {
        if (!dragging || !activeImg) return;
        applyImageWidth(
          activeImg,
          nextWidthForHandle(
            dir,
            startWidth,
            moveEvent.clientX - startX,
            moveEvent.clientY - startY,
          ),
          maxImageWidth(),
        );
        positionOverlay();
      };
      const onUp = () => {
        handle.releasePointerCapture?.(capId);
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onUp);
        handle.removeEventListener('pointercancel', onUp);
        setResizing(false);
        emitChange();
      };
      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', onUp);
      handle.addEventListener('pointercancel', onUp);
    });
  };

  const selectImage = (img) => {
    if (activeImg === img && overlay) {
      positionOverlay();
      return;
    }
    clearOverlay();
    activeImg = img;
    overlay = document.createElement('div');
    overlay.className = 'ql-image-resize-box';
    overlay.innerHTML = `
      <div class="ql-image-resize-toolbar">
        <button type="button" data-size="0.25" title="Small">S</button>
        <button type="button" data-size="0.5" title="Medium">M</button>
        <button type="button" data-size="0.75" title="Large">L</button>
        <button type="button" data-size="1" title="Full width">Full</button>
        <button type="button" data-action="delete" title="Delete image">Delete</button>
      </div>
      <span class="ql-image-resize-handle is-nw" data-dir="nw" title="Drag to resize"></span>
      <span class="ql-image-resize-handle is-n" data-dir="n" title="Drag to resize"></span>
      <span class="ql-image-resize-handle is-ne" data-dir="ne" title="Drag to resize"></span>
      <span class="ql-image-resize-handle is-e" data-dir="e" title="Drag to resize"></span>
      <span class="ql-image-resize-handle is-se" data-dir="se" title="Drag to resize"></span>
      <span class="ql-image-resize-handle is-s" data-dir="s" title="Drag to resize"></span>
      <span class="ql-image-resize-handle is-sw" data-dir="sw" title="Drag to resize"></span>
      <span class="ql-image-resize-handle is-w" data-dir="w" title="Drag to resize"></span>
    `;
    wrapperEl.appendChild(overlay);
    positionOverlay();

    overlay.querySelectorAll('[data-size]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const ratio = Number(button.getAttribute('data-size')) || 1;
        applyImageWidth(activeImg, maxImageWidth() * ratio, maxImageWidth());
        positionOverlay();
        emitChange();
      });
    });
    overlay.querySelector('[data-action="delete"]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      deleteActiveImage();
    });
    overlay.querySelectorAll('.ql-image-resize-handle').forEach(bindHandle);
  };

  const onEditorClick = (event) => {
    const img = event.target?.closest?.('img');
    if (img && editor.root.contains(img)) {
      selectImage(img);
      return;
    }
    if (!event.target?.closest?.('.ql-image-resize-box')) {
      clearOverlay();
    }
  };

  const onKeyDown = (event) => {
    if (!activeImg) return;
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;
    const tag = String(event.target?.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    event.preventDefault();
    event.stopPropagation();
    deleteActiveImage();
  };

  const onDragStart = (event) => {
    if (event.target?.tagName === 'IMG') {
      event.preventDefault();
    }
  };

  const scrollRoot = editor.root.parentElement;
  const onScrollOrResize = () => positionOverlay();

  editor.root.addEventListener('click', onEditorClick);
  editor.root.addEventListener('dragstart', onDragStart);
  document.addEventListener('keydown', onKeyDown, true);
  scrollRoot?.addEventListener('scroll', onScrollOrResize);
  window.addEventListener('resize', onScrollOrResize);

  return () => {
    editor.root.removeEventListener('click', onEditorClick);
    editor.root.removeEventListener('dragstart', onDragStart);
    document.removeEventListener('keydown', onKeyDown, true);
    scrollRoot?.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
    clearOverlay();
  };
};

const insertImageIntoQuill = async (editor, file) => {
  if (!editor || !file) return null;
  const token = localStorage.getItem('token');
  const results = await uploadFilesWithProgress({
    apiUrl: API_URL,
    token,
    files: [file],
  });
  const uploaded = results?.[0]?.data;
  const fileKey = uploaded?.fileUrl || uploaded?.fileKey || uploaded?.url;
  if (!fileKey) throw new Error('Upload failed');
  const imageUrl = buildLegacyFileUrl(fileKey) || fileKey;
  const range = editor.getSelection(true);
  const index = range ? range.index : Math.max(0, editor.getLength() - 1);
  editor.insertEmbed(index, 'image', imageUrl, 'user');
  try {
    const [leaf] = editor.getLeaf(index) || [];
    const node = leaf?.domNode;
    if (node && String(node.tagName || '').toUpperCase() === 'IMG') {
      node.setAttribute('data-file-key', extractInternalFileKey(fileKey) || fileKey);
    }
  } catch {
    // ignore
  }
  editor.setSelection(index + 1, 0);
  return {
    id: uploaded?.id || `${Date.now()}`,
    fileName: uploaded?.fileName || file.name || 'image',
    fileUrl: fileKey,
    fileType: uploaded?.fileType || 'IMAGE',
    fileSize: uploaded?.fileSize || file.size || 0,
  };
};

/**
 * Compact Quill editor with image S/M/L resize and @user / #channel mentions.
 */
const CompactRichEditor = ({
  quillRef,
  value,
  onChange,
  onPasteFiles,
  onKeepUploadedFile,
  onSensitiveClick,
  placeholder,
  minHeight = 88,
  maxHeight = 160,
  instanceClass = 'compact-rich-editor',
  enableMentions = true,
  toolbarMode = 'compact',
  channelMemberIds = [],
  uncontrolled = false,
  enableHtmlSourceToggle = false,
  onActivate,
}) => {
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);
  const imageResizingRef = useRef(false);
  const [tableSizeOpen, setTableSizeOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableWorkspaceNode, setTableWorkspaceNode] = useState(null);
  const { user: currentUser } = useSelector((state) => state.auth);
  const { users = [] } = useSelector((state) => state.users);
  const { channels = [] } = useSelector((state) => state.channels);
  const onChangeRef = useRef(onChange);
  const onKeepUploadedFileRef = useRef(onKeepUploadedFile);
  const valueRef = useRef(value);
  onChangeRef.current = onChange;
  onKeepUploadedFileRef.current = onKeepUploadedFile;
  valueRef.current = value;

  const emitHtml = () => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor || typeof onChangeRef.current !== 'function') return;
    onChangeRef.current(sanitizeEditorHtml(editor.root.innerHTML));
  };

  const keepUploaded = (uploaded) => {
    if (uploaded) onKeepUploadedFileRef.current?.(uploaded);
  };

  const [showMentions, setShowMentions] = useState(false);
  const [mentionType, setMentionType] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionMenuStyle, setMentionMenuStyle] = useState(null);

  const updateMentionMenuPosition = useCallback(() => {
    const anchor = wrapperRef.current;
    if (!anchor) {
      setMentionMenuStyle(null);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const viewportH = window.innerHeight || 0;
    const viewportW = window.innerWidth || 0;
    const spaceBelow = viewportH - rect.bottom - MENTION_LIST_GAP;
    const spaceAbove = rect.top - MENTION_LIST_GAP;
    const openUpward = spaceBelow < Math.min(MENTION_LIST_MAX_HEIGHT, 140)
      && spaceAbove > spaceBelow;
    const menuWidth = 256; // same as chat timeline w-64
    const left = Math.max(8, Math.min(rect.left, viewportW - menuWidth - 8));
    const maxHeight = Math.max(
      96,
      Math.min(MENTION_LIST_MAX_HEIGHT, openUpward ? spaceAbove : spaceBelow),
    );

    if (openUpward) {
      setMentionMenuStyle({
        position: 'fixed',
        left,
        width: menuWidth,
        bottom: viewportH - rect.top + MENTION_LIST_GAP,
        top: 'auto',
        maxHeight,
        zIndex: MENTION_LIST_Z_INDEX,
      });
    } else {
      setMentionMenuStyle({
        position: 'fixed',
        left,
        width: menuWidth,
        top: rect.bottom + MENTION_LIST_GAP,
        bottom: 'auto',
        maxHeight,
        zIndex: MENTION_LIST_Z_INDEX,
      });
    }
  }, []);

  useEffect(() => {
    if (!enableMentions) return;
    if (!users.length) dispatch(fetchUsers());
    if (!channels.length) dispatch(fetchChannels());
  }, [enableMentions, users.length, channels.length, dispatch]);

  useLayoutEffect(() => {
    if (!showMentions || mentionSuggestions.length === 0) {
      setMentionMenuStyle(null);
      return undefined;
    }
    updateMentionMenuPosition();
    const onReposition = () => updateMentionMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [showMentions, mentionSuggestions.length, updateMentionMenuPosition]);

  const mentionableChannels = useMemo(
    () => (channels || []).filter((channel) => !isDirectChannel(channel)),
    [channels],
  );

  const handleImageButton = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const editor = quillRef.current?.getEditor();
        const uploaded = await insertImageIntoQuill(editor, file);
        keepUploaded(uploaded);
        emitHtml();
      } catch (err) {
        console.error(err);
      }
    };
    input.click();
  }, [quillRef]);

  const handleCopyText = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const selection = editor.getSelection(true);
    const text = selection && selection.length
      ? editor.getText(selection.index, selection.length)
      : editor.getText();
    const plain = String(text || '').replace(/\u00a0/g, ' ').trimEnd();
    if (!plain.trim()) return;
    copyTextToClipboard(plain).catch(() => {});
  }, [quillRef]);

  const handleChecklist = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const cell = getLastNoteTableCell(editor);
    const table = cell?.closest?.('.note-data-table');
    if (cell && table && editor.root.contains(table)) {
      const rowIndex = Number.parseInt(cell.getAttribute('data-r'), 10);
      const colIndex = Number.parseInt(cell.getAttribute('data-c'), 10);
      if (Number.isFinite(rowIndex) && Number.isFinite(colIndex)) {
        syncTableNode(table);
        const data = readTableFromDom(table);
        const rows = Array.isArray(data?.rows) ? data.rows.map((row) => [...(row || [])]) : [];
        if (!rows[rowIndex]) rows[rowIndex] = [];
        const current = String(rows[rowIndex][colIndex] ?? '');
        rows[rowIndex][colIndex] = toggleCheckboxPrefix(current);
        applyTableDataToNode(table, { ...data, rows });
        emitHtml();
        requestAnimationFrame(() => {
          const nextCell = table.querySelector(`[data-r="${rowIndex}"][data-c="${colIndex}"]`);
          nextCell?.focus?.();
        });
        return;
      }
    }
    const range = editor.getSelection(true) || { index: 0, length: 0 };
    const length = Math.max(range.length, 1);
    const formats = editor.getFormat(range.index, length);
    const alreadyCheck = formats.list === 'unchecked'
      || formats.list === 'checked'
      || formats.list === 'check';
    editor.formatLine(
      range.index,
      length,
      'list',
      alreadyCheck ? false : 'unchecked',
      'user',
    );
  }, [quillRef]);

  const handleRemoveEmptyLines = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    let index = editor.getLength() - 1;
    while (index > 0) {
      const lineInfo = editor.getLine(index);
      const line = Array.isArray(lineInfo) ? lineInfo[0] : null;
      const offset = Array.isArray(lineInfo) ? lineInfo[1] : 0;
      if (!line) break;
      const lineStart = index - offset;
      const lineLength = typeof line.length === 'function' ? line.length() : 1;
      const formats = editor.getFormat(lineStart, Math.max(1, lineLength - 1));
      const inCode = Boolean(formats['code-block']);
      const contents = editor.getContents(lineStart, lineLength);
      const hasEmbed = (contents.ops || []).some((op) => op.insert && typeof op.insert === 'object');
      const text = editor.getText(lineStart, Math.max(0, lineLength - 1))
        .replace(/\u00a0/g, ' ')
        .trim();
      const empty = !inCode && !hasEmbed && !text;
      if (empty && editor.getLength() - lineLength >= 1) {
        editor.deleteText(lineStart, lineLength, 'user');
      }
      index = lineStart - 1;
    }
    emitHtml();
  }, [quillRef]);

  const handleInsertTable = useCallback((data) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    insertNoteTableAtSelection(editor, data);
    emitHtml();
  }, [quillRef]);

  const handleTableButton = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    if (range?.length) {
      const fromSelection = quillSelectionToTableData(editor, range);
      if (fromSelection) {
        handleInsertTable(fromSelection);
        return;
      }
      const selected = String(editor.getText(range.index, range.length) || '').trim();
      if (selected) {
        handleInsertTable(textToTableData(selected));
        return;
      }
    }
    setTableRows(3);
    setTableCols(3);
    setTableSizeOpen(true);
  }, [handleInsertTable, quillRef]);
  const handleImageButtonRef = useRef(handleImageButton);
  const handleTableButtonRef = useRef(handleTableButton);
  const handleCopyTextRef = useRef(handleCopyText);
  const handleChecklistRef = useRef(handleChecklist);
  const handleRemoveEmptyLinesRef = useRef(handleRemoveEmptyLines);
  const onSensitiveClickRef = useRef(onSensitiveClick);
  handleImageButtonRef.current = handleImageButton;
  handleTableButtonRef.current = handleTableButton;
  handleCopyTextRef.current = handleCopyText;
  handleChecklistRef.current = handleChecklist;
  handleRemoveEmptyLinesRef.current = handleRemoveEmptyLines;
  onSensitiveClickRef.current = onSensitiveClick;

  const modules = useMemo(() => {
    const compactToolbar = [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean'],
    ];
    const bellToolbar = [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['checklist'],
      ['table'],
    ];
    const notesToolbar = [
      ['bold', 'italic', 'underline', 'strike', 'code'],
      ['code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['checklist'],
      ['link', 'image'],
      ['copytext', 'sensitive'],
      ['table', 'emptylines', 'clean'],
    ];
    return {
      toolbar: {
        container: toolbarMode === 'notes'
          ? notesToolbar
          : (toolbarMode === 'bell' ? bellToolbar : compactToolbar),
        handlers: {
          image() {
            handleImageButtonRef.current?.();
          },
          table() {
            handleTableButtonRef.current?.();
          },
          copytext() {
            handleCopyTextRef.current?.();
          },
          checklist() {
            handleChecklistRef.current?.();
          },
          emptylines() {
            handleRemoveEmptyLinesRef.current?.();
          },
          sensitive() {
            const editor = quillRef.current?.getEditor?.();
            let selectedText = '';
            if (editor) {
              const selection = editor.getSelection(true);
              if (selection && selection.length) {
                selectedText = editor.getText(selection.index, selection.length) || '';
              }
            }
            onSensitiveClickRef.current?.(selectedText);
          },
        },
      },
    };
  }, [toolbarMode]);

  const closeMentions = useCallback(() => {
    setShowMentions(false);
    setMentionType(null);
    setMentionSuggestions([]);
    setMentionStartIndex(-1);
    setSelectedMentionIndex(0);
    setMentionMenuStyle(null);
  }, []);

  const detectMentions = useCallback((editor) => {
    if (!enableMentions || !editor) {
      closeMentions();
      return;
    }
    window.setTimeout(() => {
      const selection = editor.getSelection();
      if (!selection) {
        closeMentions();
        return;
      }
      const text = editor.getText();
      const cursorIndex = selection.index;
      const trigger = findMentionTriggerIndex(text, cursorIndex);
      if (trigger.index === -1 || !trigger.type) {
        closeMentions();
        return;
      }
      const query = text.substring(trigger.index + 1, cursorIndex).toLowerCase();

      if (trigger.type === 'user') {
        const filteredUsers = (users || [])
          .filter((u) => u.id !== currentUser?.id)
          .filter((u) => {
            if (!query) return true;
            const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
            const username = (u.username || '').toLowerCase();
            return fullName.includes(query) || username.includes(query);
          })
          .slice(0, 8);

        const everyoneMatches = !query
          || ALL_MENTION_USERNAME.startsWith(query)
          || 'everyone'.startsWith(query);
        const suggestions = [];
        if (everyoneMatches && (channelMemberIds?.length || 0) > 1) {
          suggestions.push(EVERYONE_MENTION);
        }
        suggestions.push(...filteredUsers);

        if (suggestions.length) {
          setMentionSuggestions(suggestions);
          setMentionType('user');
          setMentionStartIndex(trigger.index);
          setSelectedMentionIndex(0);
          setShowMentions(true);
        } else {
          closeMentions();
        }
        return;
      }

      const filteredChannels = mentionableChannels
        .filter((channel) => {
          if (!query) return true;
          const name = (channel.name || '').toLowerCase();
          const slug = channelMentionToken(channel).toLowerCase();
          return name.includes(query) || slug.includes(query);
        })
        .slice(0, 8);

      if (filteredChannels.length) {
        setMentionSuggestions(filteredChannels);
        setMentionType('channel');
        setMentionStartIndex(trigger.index);
        setSelectedMentionIndex(0);
        setShowMentions(true);
      } else {
        closeMentions();
      }
    }, 10);
  }, [
    enableMentions,
    users,
    currentUser?.id,
    channelMemberIds,
    mentionableChannels,
    closeMentions,
  ]);

  const insertMentionText = useCallback((mentionText) => {
    const editor = quillRef.current?.getEditor();
    if (!editor || mentionStartIndex < 0) return;
    const selection = editor.getSelection(true);
    const cursorIndex = selection ? selection.index : editor.getLength() - 1;
    const queryLength = Math.max(0, cursorIndex - mentionStartIndex);
    editor.deleteText(mentionStartIndex, queryLength);
    editor.insertText(mentionStartIndex, mentionText);
    editor.setSelection(mentionStartIndex + mentionText.length);
    closeMentions();
    if (typeof onChange === 'function') {
      onChange(sanitizeEditorHtml(editor.root.innerHTML));
    }
  }, [quillRef, mentionStartIndex, closeMentions, onChange]);

  const insertUserMention = useCallback((person) => {
    if (!person) return;
    const username = person.isEveryoneMention
      ? ALL_MENTION_USERNAME
      : (person.username || '');
    if (!username) return;
    insertMentionText(`@${username} `);
  }, [insertMentionText]);

  const insertChannelMention = useCallback((channel) => {
    const token = channelMentionToken(channel);
    if (!token) return;
    insertMentionText(`#${token} `);
  }, [insertMentionText]);

  const insertSelectedMention = useCallback(() => {
    const selected = mentionSuggestions[selectedMentionIndex];
    if (!selected) return;
    if (mentionType === 'channel') {
      insertChannelMention(selected);
      return;
    }
    insertUserMention(selected);
  }, [
    mentionSuggestions,
    selectedMentionIndex,
    mentionType,
    insertChannelMention,
    insertUserMention,
  ]);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return undefined;
    const onPaste = async (event) => {
      const inTableCell = event.target?.closest?.('.note-data-table th, .note-data-table td');
      const editor = quillRef.current?.getEditor();
      if (!inTableCell && toolbarMode === 'notes' && event.clipboardData) {
        const html = event.clipboardData.getData?.('text/html') || '';
        const text = event.clipboardData.getData?.('text/plain') || '';
        const items = Array.from(event.clipboardData.items || []);
        const hasImage = items.some((item) => item.type && item.type.startsWith('image/'));
        if ((html.trim() || text.trim()) && !hasImage) {
          event.preventDefault();
          event.stopPropagation();
          const ok = pasteNotesClipboard(editor, event.clipboardData);
          if (!ok && text.trim()) {
            const range = editor?.getSelection?.(true) || { index: Math.max(0, (editor?.getLength?.() || 1) - 1), length: 0 };
            editor?.insertText?.(range.index, text, 'user');
          }
          emitHtml();
          return;
        }
      }
      const items = Array.from(event.clipboardData?.items || []);
      const pastedFiles = Array.from(event.clipboardData?.files || []);
      const imageItems = items.filter((item) => item.type && item.type.startsWith('image/'));
      const otherFiles = pastedFiles.filter((file) => file && !(file.type || '').startsWith('image/'));
      if (!imageItems.length && !otherFiles.length) return;
      if (imageItems.length || (otherFiles.length && onPasteFiles)) {
        event.preventDefault();
        event.stopPropagation();
      }
      for (const item of imageItems) {
        const file = item.getAsFile();
        if (!file) continue;
        try {
          const uploaded = await insertImageIntoQuill(editor, file);
          keepUploaded(uploaded);
        } catch (err) {
          console.error(err);
        }
      }
      if (imageItems.length) emitHtml();
      if (otherFiles.length && onPasteFiles) {
        onPasteFiles(otherFiles);
      }
    };
    const onSelectAll = (event) => {
      if (toolbarMode !== 'notes') return;
      if (!(event.ctrlKey || event.metaKey) || String(event.key || '').toLowerCase() !== 'a') return;
      const editor = quillRef.current?.getEditor?.();
      if (!editor?.root || !root.contains(editor.root)) return;
      if (!root.contains(event.target) && document.activeElement !== editor.root) return;
      event.preventDefault();
      event.stopPropagation();
      editor.focus();
      editor.setSelection(0, Math.max(0, editor.getLength()), 'silent');
    };
    root.addEventListener('paste', onPaste, true);
    root.addEventListener('keydown', onSelectAll, true);
    const onDragOver = (event) => {
      if (![...event.dataTransfer.types].includes('Files')) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    };
    const onDrop = async (event) => {
      const files = Array.from(event.dataTransfer?.files || []);
      if (!files.length) return;
      event.preventDefault();
      event.stopPropagation();
      const editor = quillRef.current?.getEditor();
      const images = files.filter((file) => (file.type || '').startsWith('image/'));
      const others = files.filter((file) => !(file.type || '').startsWith('image/'));
      for (const file of images) {
        try {
          const uploaded = await insertImageIntoQuill(editor, file);
          keepUploaded(uploaded);
        } catch (err) {
          console.error(err);
        }
      }
      if (images.length) emitHtml();
      if (others.length && onPasteFiles) onPasteFiles(others);
    };
    root.addEventListener('dragover', onDragOver);
    root.addEventListener('drop', onDrop);
    return () => {
      root.removeEventListener('paste', onPaste, true);
      root.removeEventListener('keydown', onSelectAll, true);
      root.removeEventListener('dragover', onDragOver);
      root.removeEventListener('drop', onDrop);
    };
  }, [quillRef, onPasteFiles, toolbarMode]);

  useEffect(() => {
    let tries = 0;
    let timer = 0;
    const attach = () => {
      const wrap = wrapperRef.current;
      const button = wrap?.querySelector('button.ql-emptylines');
      if (!button) {
        if (tries < 20) {
          tries += 1;
          timer = window.setTimeout(attach, 50);
        }
        return;
      }
      button.setAttribute('title', 'Remove empty lines');
      button.setAttribute('aria-label', 'Remove empty lines');
      const tableBtn = wrap.querySelector('button.ql-table');
      if (tableBtn) {
        tableBtn.setAttribute('title', 'Insert table');
        tableBtn.setAttribute('aria-label', 'Insert table');
      }
    };
    attach();
    return () => window.clearTimeout(timer);
  }, [toolbarMode]);

  useEffect(() => {
    let cleanup = () => {};
    let tries = 0;
    const attach = () => {
      const editor = quillRef.current?.getEditor();
      const wrapper = wrapperRef.current;
      if (!editor?.root || !wrapper) {
        if (tries < 20) {
          tries += 1;
          window.setTimeout(attach, 50);
        }
        return;
      }
      registerNoteTableBlot(editor.constructor);
      ensureNoteTableOnEditor(editor);
      restoreImageWidths(editor.root, valueRef.current);
      hydrateEditorImages(editor.root);
      window.requestAnimationFrame(() => {
        restoreImageWidths(editor.root, valueRef.current);
        hydrateEditorImages(editor.root);
      });
      const imageCleanup = attachQuillImageResize(editor, wrapper, () => {
        emitHtml();
      }, imageResizingRef);
      const Delta = Quill.import('delta');
      editor.clipboard.addMatcher('TABLE', (node, delta) => {
        const data = htmlTableToTableData(node);
        if (!data) return delta;
        return new Delta().insert({ noteTable: data });
      });
      editor.clipboard.addMatcher('DIV', (node, delta) => {
        if (!node?.classList?.contains('note-data-table')) return delta;
        return new Delta().insert({ noteTable: readTableFromDom(node) });
      });
      const tableCleanup = attachNoteTableChrome(
        editor,
        (_data, table) => setTableWorkspaceNode(table),
        () => emitHtml(),
      );
      editor.root.querySelectorAll('.note-data-table').forEach((node) => {
        applyTableDataToNode(node, readTableFromDom(node));
      });
      const container = wrapper.querySelector('.ql-container') || wrapper;
      const chromeCleanup = mountNoteCodeChrome(container, editor.root);
      const onTextChange = () => {
        window.requestAnimationFrame(() => {
          syncNoteChecklistHighlights(editor.root);
          syncNoteCodeChrome(editor.root);
        });
      };
      const onChecklistToggle = (event) => {
        const item = event.target?.closest?.('li[data-list="checked"], li[data-list="unchecked"], li.note-check-on');
        if (!item) return;
        window.requestAnimationFrame(() => {
          syncNoteChecklistHighlights(editor.root);
          emitHtml();
        });
      };
      syncNoteChecklistHighlights(editor.root);
      editor.on('text-change', onTextChange);
      editor.root.addEventListener('mouseup', onChecklistToggle);
      editor.root.addEventListener('click', onChecklistToggle);
      editor.root.addEventListener('touchend', onChecklistToggle);
      cleanup = () => {
        editor.off('text-change', onTextChange);
        editor.root.removeEventListener('mouseup', onChecklistToggle);
        editor.root.removeEventListener('click', onChecklistToggle);
        editor.root.removeEventListener('touchend', onChecklistToggle);
        chromeCleanup();
        tableCleanup();
        imageCleanup();
      };
    };
    attach();
    return () => cleanup();
  }, [quillRef]);

  useEffect(() => {
    if (!enableMentions) return undefined;
    let tries = 0;
    let editorEl = null;
    let handleKeyDown = null;

    const attach = () => {
      const editor = quillRef.current?.getEditor();
      if (!editor?.root) {
        if (tries < 20) {
          tries += 1;
          window.setTimeout(attach, 50);
        }
        return;
      }
      editorEl = editor.root;
      handleKeyDown = (event) => {
        if (!showMentions || mentionSuggestions.length === 0) return;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          event.stopPropagation();
          setSelectedMentionIndex((prev) =>
            prev < mentionSuggestions.length - 1 ? prev + 1 : prev);
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          event.stopPropagation();
          setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0));
          return;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          event.stopPropagation();
          insertSelectedMention();
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeMentions();
        }
      };
      editorEl.addEventListener('keydown', handleKeyDown, true);
    };
    attach();
    return () => {
      if (editorEl && handleKeyDown) {
        editorEl.removeEventListener('keydown', handleKeyDown, true);
      }
    };
  }, [
    enableMentions,
    quillRef,
    showMentions,
    mentionSuggestions,
    insertSelectedMention,
    closeMentions,
  ]);

  const [htmlSourceMode, setHtmlSourceMode] = useState(false);
  const [htmlSourceDraft, setHtmlSourceDraft] = useState('');
  const [htmlPreviewMode, setHtmlPreviewMode] = useState(false);
  const htmlPreviewRef = useRef(null);
  const htmlPreviewFocusedRef = useRef(false);

  const looksLikeCustomHtml = (html) => {
    const raw = String(html || '').trim();
    if (!raw) return false;
    return /<(div|table|section|article|h[1-6]|hr)\b/i.test(raw)
      || /\sstyle\s*=/i.test(raw)
      || raw.includes('&lt;div')
      || raw.includes('&lt;table');
  };

  const currentEditorHtml = () => {
    const editor = quillRef.current?.getEditor?.();
    if (editor?.root?.innerHTML) return editor.root.innerHTML;
    return typeof value === 'string' ? value : '';
  };

  const formatHtmlSource = (html) => {
    const raw = String(html || '').trim();
    if (!raw) return '';
    return raw.replace(/></g, '>\n<');
  };

  const resolvePreviewHtml = (html) => {
    const raw = String(html || htmlSourceDraft || '').trim();
    if (typeof document === 'undefined') return raw;
    const holder = document.createElement('div');
    holder.innerHTML = raw;
    const text = String(holder.textContent || '').trim();
    if (text.startsWith('<') && /<\/[a-z]/i.test(text)) {
      holder.innerHTML = text;
    }
    return holder.innerHTML;
  };

  useEffect(() => {
    if (enableHtmlSourceToggle && looksLikeCustomHtml(value)) {
      setHtmlPreviewMode(true);
    }
  }, [enableHtmlSourceToggle, value]);

  useLayoutEffect(() => {
    if (htmlSourceMode || !htmlPreviewMode) return;
    const el = htmlPreviewRef.current;
    if (!el || htmlPreviewFocusedRef.current) return;
    const next = resolvePreviewHtml(typeof value === 'string' ? value : '');
    if (el.innerHTML !== next) {
      el.innerHTML = next;
    }
  }, [htmlSourceMode, htmlPreviewMode, value]);

  const emitPreviewHtml = () => {
    const html = htmlPreviewRef.current?.innerHTML || '';
    onChangeRef.current?.(html);
  };

  const toggleHtmlSourceMode = () => {
    if (!htmlSourceMode) {
      let raw = htmlPreviewMode && htmlPreviewRef.current
        ? htmlPreviewRef.current.innerHTML
        : (htmlPreviewMode ? (typeof value === 'string' ? value : '') : currentEditorHtml());
      if (typeof document !== 'undefined' && raw) {
        const holder = document.createElement('div');
        holder.innerHTML = raw;
        const text = String(holder.textContent || '').trim();
        if (text.startsWith('<') && /<\/[a-z]/i.test(text)) {
          raw = text;
        }
      }
      setHtmlSourceDraft(formatHtmlSource(raw));
      setHtmlSourceMode(true);
      setHtmlPreviewMode(true);
      return;
    }
    const next = htmlSourceDraft;
    onChangeRef.current?.(next);
    htmlPreviewFocusedRef.current = false;
    setHtmlSourceMode(false);
    setHtmlPreviewMode(true);
  };

  const handleChange = (content, _delta, source) => {
    if (imageResizingRef.current) return;
    if (source === 'user') {
      onChange?.(sanitizeEditorHtml(content));
    }
    window.requestAnimationFrame(() => {
      syncNoteCodeChrome(quillRef.current?.getEditor?.()?.root);
    });
    if (source === 'user' && enableMentions) {
      const editor = quillRef.current?.getEditor?.();
      if (editor) detectMentions(editor);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={`${instanceClass} relative border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 ${toolbarMode === 'notes' ? 'h-full min-h-0 overflow-hidden' : 'overflow-visible'}`}
      onMouseDown={(event) => {
        if (!onActivate) return;
        if (event.target.closest?.('.ql-toolbar')) return;
        onActivate();
      }}
    >
      {enableHtmlSourceToggle && (
        <div className="flex items-center justify-end px-2 py-1.5 border-b border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={toggleHtmlSourceMode}
            className={`text-[11px] font-medium rounded-md px-2.5 py-1 ${
              htmlSourceMode
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {htmlSourceMode ? 'Show formatted' : 'Convert to HTML'}
          </button>
        </div>
      )}
      {htmlSourceMode ? (
        <textarea
          value={htmlSourceDraft}
          onChange={(e) => {
            const next = e.target.value;
            setHtmlSourceDraft(next);
            onChangeRef.current?.(next);
          }}
          spellCheck={false}
          className="w-full resize-y overflow-auto bg-gray-950 text-emerald-300 font-mono text-[11px] leading-5 px-3 py-2.5 outline-none dark:bg-black"
          style={{ minHeight: `${Math.max(minHeight, 160)}px`, maxHeight: `${Math.max(maxHeight, 280)}px` }}
          aria-label="HTML source"
        />
      ) : htmlPreviewMode ? (
        <div
          ref={htmlPreviewRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label="Formatted message"
          onFocus={() => {
            htmlPreviewFocusedRef.current = true;
          }}
          onBlur={() => {
            htmlPreviewFocusedRef.current = false;
            emitPreviewHtml();
          }}
          onInput={emitPreviewHtml}
          className="overflow-auto px-3 py-3 bg-white text-gray-900 outline-none cursor-text"
          style={{ minHeight: `${Math.max(minHeight, 160)}px`, maxHeight: `${Math.max(maxHeight, 320)}px` }}
        />
      ) : (
      <ReactQuill
        ref={quillRef}
        theme="snow"
        {...(uncontrolled
          ? { defaultValue: typeof value === 'string' ? value : '' }
          : { value: typeof value === 'string' ? value : '' })}
        onChange={handleChange}
        modules={modules}
        formats={COMPACT_QUILL_FORMATS}
        placeholder={placeholder}
        className="bg-transparent"
      />
      )}

      {enableMentions && showMentions && mentionSuggestions.length > 0 && mentionMenuStyle
        ? ReactDOM.createPortal(
          <div
            className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg overflow-y-auto"
            style={mentionMenuStyle}
            data-mention-suggestions
          >
            {mentionType === 'channel'
              ? mentionSuggestions.map((channel, index) => (
                <button
                  key={channel.id || channelMentionToken(channel)}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertChannelMention(channel)}
                  className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                    index === selectedMentionIndex
                      ? 'bg-primary-50 dark:bg-primary-900/20'
                      : ''
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    #
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {channel.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      #{channelMentionToken(channel)}
                      {channel.isPrivate ? ' · Private' : ''}
                    </p>
                  </div>
                </button>
              ))
              : mentionSuggestions.map((person, index) => {
                const fullName = `${person.firstName || ''} ${person.lastName || ''}`.trim();
                return (
                  <button
                    key={person.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertUserMention(person)}
                    className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                      index === selectedMentionIndex
                        ? 'bg-primary-50 dark:bg-primary-900/20'
                        : ''
                    }`}
                  >
                    {person.isEveryoneMention ? (
                      <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        @
                      </div>
                    ) : (
                      <PresignedAvatar
                        fileKey={person.profilePicture}
                        name={fullName || person.username}
                        initials={`${person.firstName?.[0] || ''}${person.lastName?.[0] || ''}`}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        fallbackClassName="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {person.isEveryoneMention ? 'all' : (fullName || person.username)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {person.isEveryoneMention
                          ? `Notify everyone in this channel (${Math.max((channelMemberIds?.length || 1) - 1, 0)})`
                          : `@${person.username}`}
                      </p>
                    </div>
                  </button>
                );
              })}
          </div>,
          document.body,
        )
        : null}

      {tableSizeOpen ? ReactDOM.createPortal(
        <div className="fixed inset-0 z-[2100] overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setTableSizeOpen(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-sm rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Insert table</h3>
                <button
                  type="button"
                  onClick={() => setTableSizeOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Choose size, or select text first to convert it into a table.
                </p>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Rows
                  <input
                    type="number"
                    min={1}
                    max={MAX_ROWS}
                    value={tableRows}
                    onChange={(e) => setTableRows(clampTableDim(e.target.value, 1, MAX_ROWS))}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  />
                </label>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Columns
                  <input
                    type="number"
                    min={1}
                    max={MAX_COLS}
                    value={tableCols}
                    onChange={(e) => setTableCols(clampTableDim(e.target.value, 1, MAX_COLS))}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setTableSizeOpen(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleInsertTable(emptyTableData(tableRows, tableCols));
                    setTableSizeOpen(false);
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}

      {tableWorkspaceNode ? (
        <NoteTableWorkspaceModal
          tableNode={tableWorkspaceNode}
          onClose={() => setTableWorkspaceNode(null)}
          onCommit={emitHtml}
        />
      ) : null}

      <style>{`
        .${instanceClass} .ql-container {
          font-size: 14px;
          min-height: ${minHeight}px;
          max-height: ${maxHeight}px;
          overflow-y: auto !important;
          overflow-x: auto !important;
          border: none !important;
        }
        .${instanceClass} .ql-editor {
          min-height: ${minHeight}px;
          color: #111827;
          overflow-x: auto;
          word-wrap: break-word;
          word-break: break-word;
          padding: 10px 12px !important;
        }
        .notes-rich-editor {
          display: flex;
          flex-direction: column;
        }
        .notes-rich-editor > .quill {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          height: 100%;
        }
        .notes-rich-editor .ql-toolbar {
          position: sticky;
          top: 0;
          z-index: 30;
          flex-shrink: 0;
          background: #fff;
        }
        .dark .notes-rich-editor .ql-toolbar {
          background: #1f2937;
        }
        .notes-rich-editor .ql-container {
          flex: 1 1 auto;
          min-height: 0 !important;
          max-height: none !important;
          overflow-y: auto !important;
        }
        .notes-rich-editor .ql-container,
        .notes-rich-editor .ql-editor {
          font-family: Figtree, Inter, system-ui, sans-serif;
          font-size: 16px;
          line-height: 1.65;
        }
        .notes-rich-editor .ql-editor {
          padding: 14px 16px !important;
          min-height: 100% !important;
        }
        .${instanceClass} .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          cursor: pointer;
        }
        .${instanceClass} .ql-editor img[data-width] {
          height: auto !important;
        }
        .${instanceClass} .ql-editor a {
          color: #2563eb;
          text-decoration: underline;
          word-break: break-all;
        }
        .dark .${instanceClass} .ql-editor a {
          color: #93c5fd;
        }
        .${instanceClass} .ql-image-resize-box {
          position: absolute;
          z-index: 20;
          border: 2px solid #6366f1;
          border-radius: 8px;
          box-sizing: border-box;
          pointer-events: none;
        }
        .${instanceClass} .ql-image-resize-toolbar {
          position: absolute;
          left: 50%;
          top: -40px;
          transform: translateX(-50%);
          display: flex;
          gap: 4px;
          pointer-events: auto;
          background: #111827;
          border-radius: 999px;
          padding: 3px 5px;
        }
        .${instanceClass} .ql-image-resize-toolbar button {
          min-width: 22px;
          height: 20px;
          padding: 0 6px;
          border: 0;
          border-radius: 999px;
          background: transparent;
          color: #f9fafb;
          font-size: 10px;
          font-weight: 700;
          line-height: 20px;
          cursor: pointer;
        }
        .${instanceClass} .ql-image-resize-toolbar button:hover {
          background: #4f46e5;
        }
        .${instanceClass} .ql-image-resize-toolbar button[data-action="delete"] {
          color: #fecaca;
        }
        .${instanceClass} .ql-image-resize-toolbar button[data-action="delete"]:hover {
          background: #be123c;
          color: #fff;
        }
        .${instanceClass} .ql-image-resize-handle {
          position: absolute;
          width: 14px;
          height: 14px;
          border-radius: 3px;
          background: #6366f1;
          border: 2px solid #fff;
          pointer-events: auto;
          box-sizing: border-box;
          z-index: 2;
        }
        .${instanceClass} .ql-image-resize-handle.is-nw { left: -7px; top: -7px; cursor: nwse-resize; }
        .${instanceClass} .ql-image-resize-handle.is-n { left: 50%; top: -7px; margin-left: -7px; cursor: ns-resize; }
        .${instanceClass} .ql-image-resize-handle.is-ne { right: -7px; top: -7px; cursor: nesw-resize; }
        .${instanceClass} .ql-image-resize-handle.is-e { right: -7px; top: 50%; margin-top: -7px; cursor: ew-resize; }
        .${instanceClass} .ql-image-resize-handle.is-se { right: -7px; bottom: -7px; cursor: nwse-resize; }
        .${instanceClass} .ql-image-resize-handle.is-s { left: 50%; bottom: -7px; margin-left: -7px; cursor: ns-resize; }
        .${instanceClass} .ql-image-resize-handle.is-sw { left: -7px; bottom: -7px; cursor: nesw-resize; }
        .${instanceClass} .ql-image-resize-handle.is-w { left: -7px; top: 50%; margin-top: -7px; cursor: ew-resize; }
        .${instanceClass} .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        .dark .${instanceClass} .ql-editor {
          color: #f9fafb;
        }
        .${instanceClass} .ql-toolbar {
          border-bottom: 1px solid #e5e7eb;
          border-left: none;
          border-right: none;
          border-top: none;
          overflow-x: hidden !important;
          white-space: normal !important;
          display: flex !important;
          flex-wrap: wrap !important;
          padding: 6px 8px;
        }
        .${instanceClass} .ql-toolbar .ql-formats {
          display: inline-flex !important;
          flex-wrap: wrap !important;
        }
        .dark .${instanceClass} .ql-toolbar {
          background: #1f2937;
          border-bottom-color: #4b5563;
        }
        .${instanceClass} .ql-toolbar button.ql-copytext,
        .${instanceClass} .ql-toolbar button.ql-sensitive,
        .${instanceClass} .ql-toolbar button.ql-checklist {
          width: auto !important;
          padding: 0 8px;
        }
        .${instanceClass} .ql-toolbar button.ql-table {
          color: #444;
        }
        .dark .${instanceClass} .ql-toolbar button.ql-table {
          color: #d1d5db;
        }
        .${instanceClass} .ql-toolbar button.ql-table:hover {
          color: #06c;
        }
        .${instanceClass} .ql-toolbar button.ql-table::after {
          content: '';
          display: block;
          width: 16px;
          height: 16px;
          background-color: currentColor;
          -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='black' stroke-width='2'%3E%3Cpath d='M3 3h18v18H3z'/%3E%3Cpath d='M3 9h18M3 15h18M9 3v18M15 3v18'/%3E%3C/svg%3E") center / contain no-repeat;
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='black' stroke-width='2'%3E%3Cpath d='M3 3h18v18H3z'/%3E%3Cpath d='M3 9h18M3 15h18M9 3v18M15 3v18'/%3E%3C/svg%3E") center / contain no-repeat;
        }
        .${instanceClass} .ql-editor .note-data-table {
          position: relative;
          margin: 12px 0 8px;
          overflow-x: auto;
          padding-top: 36px;
        }
        .${instanceClass} .ql-editor .note-data-table .note-table {
          width: auto;
          max-width: none;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 13px;
        }
        .${instanceClass} .ql-editor .note-data-table th,
        .${instanceClass} .ql-editor .note-data-table td {
          border: 1px solid #d1d5db;
          padding: 6px 8px;
          min-width: 0;
          vertical-align: top;
          background: #fff;
          position: relative;
          overflow: hidden;
        }
        .${instanceClass} .ql-editor .note-data-table th {
          background: #f3f4f6;
          font-weight: 700;
        }
        .dark .${instanceClass} .ql-editor .note-data-table th,
        .dark .${instanceClass} .ql-editor .note-data-table td {
          border-color: #4b5563;
          background: #111827;
          color: #f9fafb;
        }
        .dark .${instanceClass} .ql-editor .note-data-table th {
          background: #1f2937;
        }
        .${instanceClass} .ql-editor .note-table-actions {
          position: absolute;
          top: 4px;
          right: 4px;
          z-index: 3;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .${instanceClass} .ql-editor .note-col-resizer {
          position: absolute;
          top: 0;
          right: -3px;
          width: 8px;
          height: 100%;
          cursor: col-resize;
          z-index: 4;
        }
        .${instanceClass} .ql-editor .note-col-resizer::after {
          content: '';
          position: absolute;
          top: 4px;
          bottom: 4px;
          left: 3px;
          width: 2px;
          border-radius: 2px;
          background: #9ca3af;
        }
        .${instanceClass} .ql-editor .note-table-add-row,
        .${instanceClass} .ql-editor .note-table-add-col,
        .${instanceClass} .ql-editor .note-table-add-header,
        .${instanceClass} .ql-editor .note-table-del-row,
        .${instanceClass} .ql-editor .note-table-del-col,
        .${instanceClass} .ql-editor .note-table-summary-btn {
          border: 0;
          cursor: pointer;
          background: #111827;
          color: #fff;
          border-radius: 8px;
        }
        .${instanceClass} .ql-editor .note-table-add-row,
        .${instanceClass} .ql-editor .note-table-add-col,
        .${instanceClass} .ql-editor .note-table-add-header,
        .${instanceClass} .ql-editor .note-table-del-row,
        .${instanceClass} .ql-editor .note-table-del-col {
          height: 26px;
          padding: 0 8px;
          font-size: 11px;
          font-weight: 700;
          line-height: 26px;
        }
        .${instanceClass} .ql-editor .note-table-summary-btn {
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .${instanceClass} .ql-editor .note-table-add-row:hover,
        .${instanceClass} .ql-editor .note-table-add-col:hover,
        .${instanceClass} .ql-editor .note-table-add-header:hover,
        .${instanceClass} .ql-editor .note-table-del-row:hover,
        .${instanceClass} .ql-editor .note-table-del-col:hover,
        .${instanceClass} .ql-editor .note-table-summary-btn:hover {
          background: #4f46e5;
        }
        .${instanceClass} .ql-editor .note-cell-check {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          margin: 2px 0;
          padding: 2px 6px 2px 4px;
          border-radius: 6px;
        }
        .${instanceClass} .ql-editor .note-cell-check.is-checked {
          background: #4ade80;
          color: #14532d;
        }
        .dark .${instanceClass} .ql-editor .note-cell-check.is-checked {
          background: rgba(34, 197, 94, 0.55);
          color: #bbf7d0;
        }
        .${instanceClass} .ql-editor .note-cell-check input {
          margin-top: 3px;
          flex-shrink: 0;
        }
        .${instanceClass} .ql-toolbar button.ql-emptylines {
          color: #444;
        }
        .dark .${instanceClass} .ql-toolbar button.ql-emptylines {
          color: #d1d5db;
        }
        .${instanceClass} .ql-toolbar button.ql-emptylines:hover {
          color: #06c;
        }
        .${instanceClass} .ql-toolbar button.ql-emptylines::after {
          content: '';
          display: block;
          width: 16px;
          height: 16px;
          background-color: currentColor;
          -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 6h16M4 18h16M8 12h8'/%3E%3Cpath d='M12 9v6'/%3E%3Cpath d='M9.5 10.5L12 8l2.5 2.5M9.5 13.5L12 16l2.5-2.5'/%3E%3C/svg%3E") center / contain no-repeat;
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 6h16M4 18h16M8 12h8'/%3E%3Cpath d='M12 9v6'/%3E%3Cpath d='M9.5 10.5L12 8l2.5 2.5M9.5 13.5L12 16l2.5-2.5'/%3E%3C/svg%3E") center / contain no-repeat;
        }
        .${instanceClass} .ql-toolbar button.ql-copytext::after {
          content: 'Copy';
          font-size: 11px;
          font-weight: 700;
        }
        .${instanceClass} .ql-toolbar button.ql-sensitive::after {
          content: 'Credentials';
          font-size: 11px;
          font-weight: 700;
        }
        .${instanceClass} .ql-toolbar button.ql-checklist::after {
          content: '☐ Checkbox';
          font-size: 11px;
          font-weight: 700;
        }
        .${instanceClass} .ql-editor li[data-list="checked"],
        .${instanceClass} .ql-editor li[data-list="unchecked"],
        .${instanceClass} .ql-editor li.note-check-on {
          list-style: none;
          position: relative;
          display: block;
          width: 100%;
          padding-left: 1.75em;
          padding-right: 8px;
          padding-top: 2px;
          padding-bottom: 2px;
          margin: 2px 0;
          border-radius: 6px;
        }
        .${instanceClass} .ql-editor li[data-list="checked"],
        .${instanceClass} .ql-editor li.note-check-on {
          background-color: transparent !important;
          background-image: linear-gradient(
            to right,
            transparent 0,
            transparent 1.65em,
            #4ade80 1.65em
          ) !important;
          color: #14532d !important;
        }
        .dark .${instanceClass} .ql-editor li[data-list="checked"],
        .dark .${instanceClass} .ql-editor li.note-check-on {
          background-image: linear-gradient(
            to right,
            transparent 0,
            transparent 1.65em,
            rgba(34, 197, 94, 0.55) 1.65em
          ) !important;
          color: #bbf7d0 !important;
        }
        .${instanceClass} .ql-editor li[data-list="checked"] > .ql-ui,
        .${instanceClass} .ql-editor li[data-list="unchecked"] > .ql-ui {
          cursor: pointer;
          color: #4b5563;
          background: transparent !important;
        }
        .${instanceClass} .ql-editor li[data-list="unchecked"] > .ql-ui:before {
          content: '\\2610';
          display: inline-block;
          margin-left: -1.6em;
          margin-right: 0.35em;
          width: 1.2em;
          text-align: center;
        }
        .${instanceClass} .ql-editor li[data-list="checked"] > .ql-ui:before,
        .${instanceClass} .ql-editor li.note-check-on > .ql-ui:before {
          content: '\\2611';
          display: inline-block;
          margin-left: -1.6em;
          margin-right: 0.35em;
          width: 1.2em;
          text-align: center;
          color: #15803d;
        }
        .${instanceClass} .ql-toolbar button.ql-list[value="check"] .ql-stroke {
          stroke: currentColor;
        }
        .${instanceClass} .ql-snow .ql-editor pre.ql-syntax,
        .${instanceClass} .ql-snow .ql-editor pre,
        .${instanceClass} .ql-editor pre.ql-syntax,
        .${instanceClass} .ql-editor .ql-code-block-container,
        .${instanceClass} .ql-editor pre {
          background: #21252b !important;
          color: #d7dae0 !important;
          border-radius: 10px !important;
          margin: 16px 0 10px !important;
          padding: 40px 44px 12px 3.25em !important;
          font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
          font-size: 13px !important;
          line-height: 22px !important;
          letter-spacing: 0;
          white-space: pre !important;
          overflow-x: auto !important;
          tab-size: 2;
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
        }
        .${instanceClass} .ql-snow .ql-editor .ql-code-block,
        .${instanceClass} .ql-editor .ql-code-block {
          background: transparent !important;
          color: #d7dae0 !important;
          font-family: inherit !important;
          font-size: inherit !important;
          line-height: inherit !important;
          padding: 0 !important;
        }
        .${instanceClass} .note-code-chrome {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 6;
          overflow: hidden;
        }
        .${instanceClass} .note-code-chrome-host {
          position: absolute;
          pointer-events: none;
        }
        .${instanceClass} .note-code-gutter {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 2.7em;
          background: #1b1f24;
          border-right: 1px solid #3b4048;
          border-radius: 10px 0 0 10px;
          color: #7d8590;
          font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 12px;
          line-height: 22px;
          text-align: right;
          user-select: none;
          box-sizing: border-box;
        }
        .${instanceClass} .note-code-gutter-num {
          box-sizing: border-box;
          padding-right: 8px;
        }
        .${instanceClass} .note-code-copy {
          position: absolute;
          top: 14px;
          left: 10px;
          z-index: 3;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          padding: 0;
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 999px;
          background: #1f2328;
          color: #f3f4f6;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
          cursor: pointer;
          pointer-events: auto;
          opacity: 0.5;
        }
        .${instanceClass} .note-code-copy svg {
          width: 15px;
          height: 15px;
        }
        .${instanceClass} .note-code-copy:hover,
        .${instanceClass} .note-code-copy[data-copied] {
          opacity: 1;
          background: #2c313a;
          color: #fff;
        }
        .${instanceClass} .note-code-lines {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }
        .${instanceClass} .note-code-line-hit {
          position: relative;
          pointer-events: none;
        }
        .${instanceClass} .note-code-copy-line {
          position: absolute;
          top: 50%;
          right: 8px;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          padding: 0;
          border: 0;
          border-radius: 4px;
          background: #2c313a;
          color: #c5cad3;
          cursor: pointer;
          opacity: 0;
          transform: translateY(-50%);
          pointer-events: auto;
        }
        .${instanceClass} .note-code-line-hit.is-hovered .note-code-copy-line,
        .${instanceClass} .note-code-copy-line:hover,
        .${instanceClass} .note-code-copy-line[data-copied] {
          opacity: 1;
          color: #fff;
          background: #4b5563;
        }
        .${instanceClass} .ql-snow .ql-editor code,
        .${instanceClass} .ql-editor code {
          background: #21252b !important;
          color: #e5c07b !important;
          border-radius: 4px;
          padding: 1px 4px;
          font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
          font-size: 0.9em;
        }
        .dark .${instanceClass} .ql-editor code {
          background: #21252b;
          color: #e5c07b;
        }
        .dark .${instanceClass} .ql-stroke { stroke: #d1d5db; }
        .dark .${instanceClass} .ql-fill { fill: #d1d5db; }
        .dark .${instanceClass} .ql-picker-label { color: #d1d5db; }
      `}</style>
    </div>
  );
};

export const collectMentionedUserIdsFromPlain = (
  plainText,
  users = [],
  currentUserId,
  memberIds = [],
) => {
  const ids = new Set();
  const text = String(plainText || '');
  const regex = /@([A-Za-z0-9._-]+(?:@[A-Za-z0-9._-]+)*)/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const token = match[1];
    if (isEveryoneMention(token)) {
      (memberIds || []).forEach((id) => {
        if (id && String(id) !== String(currentUserId)) ids.add(id);
      });
      continue;
    }
    const user = findUserByMention(users, token);
    if (user?.id && String(user.id) !== String(currentUserId)) {
      ids.add(user.id);
    }
  }
  return Array.from(ids);
};

export default CompactRichEditor;
